import { type ReactNode } from 'react'

import {
  EmergencyStatusBadge,
  type EmergencyStatusValue,
} from './EmergencyStatusBadge'
import { Icon } from './Icon'
import { Image } from './Image'
import { bedFreeLabel, type BedTypeDef } from '../lib/bedTypes'
import { hospitalPhotos } from '../lib/hospitalImages'

export interface MiniHospitalCardProps {
  name: string
  /** Area + optional distance line, e.g. "Tema · 2.4 km". */
  area: string
  /** Hospital slug, used to find that hospital's own photograph (M19-T07). */
  slug?: string
  /** Whether the emergency department is taking patients. */
  emergencyStatus: EmergencyStatusValue
  /**
   * Which bed type the count below is about, and how many are free right now.
   *
   * The card used to hard-code ICU, which was right while the home strip was
   * its only caller. M21-T02 puts it on `/beds` under a bed-type selector,
   * where the count has to follow the patient's choice — so the TYPE is the
   * caller's to state, and the card only knows how to say it.
   */
  bedType: BedTypeDef
  bedFree: number
  onSelect: () => void
}

/**
 * Compact hospital card for the home "Nearby Hospitals" strip (M12-T01,
 * ADR-0026). Fixed width so the strip scrolls horizontally on a phone; a 4-up
 * grid from `lg`, as the Figma frame draws it.
 *
 * M19-T07 gave it the photograph its sibling `HospitalCard` has had since
 * M17-T18b — this was the one hospital card that never got one — and replaced
 * the three bed badges with the two facts the owner asked for: whether the
 * emergency department is taking patients, and how many beds are free.
 *
 * M21-T01 made the bed type a prop. It was hard-coded to ICU, which was correct
 * while the home strip was the only caller; `/beds` (M21-T02) shows the type the
 * patient selected, so the caller states the type and the card says it. The home
 * pins ICU explicitly and renders exactly as before.
 *
 * TWO FIELDS THE FIGMA CARD SHOWS ARE DELIBERATELY ABSENT, and both would be
 * easy to "restore" later by someone comparing this against the frame:
 *   - a nightly rate. ADR-0027 gates price behind the bed-type choice, and the
 *     home is unfiltered, so any figure here is one we cannot stand behind.
 *   - "Average Waitime". There is no such field on `BedState` — it is invented
 *     artwork, and a fabricated clinical wait is a safety problem rather than a
 *     copy one.
 * A test asserts both stay out.
 */
export function MiniHospitalCard({
  name,
  area,
  slug,
  emergencyStatus,
  bedType,
  bedFree,
  onSelect,
}: MiniHospitalCardProps): ReactNode {
  const photos = slug ? hospitalPhotos(slug) : null

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-52 shrink-0 flex-col gap-1.5 rounded-r3 border border-line-1 bg-bg-app p-2.5 text-left shadow-xs transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:w-auto"
    >
      {/* EVERY PHOTO ONE SHAPE: 16:9, cropped to fill and centred (owner,
          2026-10-21). The frame used to take each photograph's own
          proportion -- the `aspect-[350/324]` class here lost to the Image
          component's inline default -- so Britannia's photo, shaped
          differently from the rest, made its card taller. A hospital with no
          mapped photograph keeps the neutral placeholder, in the same shape. */}
      {photos ? (
        <Image
          asset={photos.main.asset}
          alt={photos.main.alt}
          aspectRatio="16 / 9"
          className="w-full rounded-r2"
        />
      ) : (
        <div className="grid aspect-video w-full place-items-center overflow-hidden rounded-r2 bg-gradient-to-br from-bg-soft to-bg-sunken text-ink-4">
          <Icon name="bed" size={26} />
        </div>
      )}

      {/* Same language as HospitalCard, one step down each: the strip is a
          scannable teaser beside the full card, not a competing one. */}
      <span className="truncate text-[16px] font-medium leading-6 text-ink-1">
        {name}
      </span>
      <span className="inline-flex items-center gap-1 text-[12px] leading-4 text-ink-2">
        <Icon name="pin" size={12} /> {area}
      </span>
      <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <EmergencyStatusBadge status={emergencyStatus} />
        <span className="text-[12px] leading-4 text-ink-2">
          {bedFreeLabel(bedType, bedFree)}
        </span>
      </span>
    </button>
  )
}
